import React, { useState, useRef, useEffect } from 'react';

interface Props {
  topContent: React.ReactNode;
  bottomContent: React.ReactNode;
  onSplitRatioChange?: (ratio: number) => void;
}

const HANDLE_HEIGHT = 20;
const INITIAL_SPLIT_RATIO = 0.45;

export function SplitScreen({ topContent, bottomContent, onSplitRatioChange }: Props): React.ReactElement {
  const [containerHeight, setContainerHeight] = useState(0);
  const [splitRatio, setSplitRatio] = useState(INITIAL_SPLIT_RATIO);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startRatioRef = useRef(INITIAL_SPLIT_RATIO);

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.clientHeight;
      if (height > 0) {
        setContainerHeight(height);
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (onSplitRatioChange) {
      onSplitRatioChange(splitRatio);
    }
  }, [splitRatio, onSplitRatioChange]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startYRef.current = e.clientY;
    startRatioRef.current = splitRatio;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startYRef.current = e.touches[0].clientY;
    startRatioRef.current = splitRatio;
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (clientY: number) => {
      if (containerHeight === 0) return;

      const deltaY = clientY - startYRef.current;
      const deltaRatio = deltaY / containerHeight;
      const newRatio = Math.max(0, Math.min(0.9, startRatioRef.current + deltaRatio));

      setSplitRatio(newRatio);
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, containerHeight]);

  const topHeight = containerHeight * splitRatio;

  const topSectionStyle: React.CSSProperties = {
    ...styles.topSection,
    height: `${topHeight}px`,
  };

  const handleStyle: React.CSSProperties = {
    ...styles.handle,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={topSectionStyle}>
        <div style={styles.topContentWrapper}>
          {topContent}
        </div>
      </div>

      <div
        style={handleStyle}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div style={styles.handleBar} />
      </div>

      <div style={styles.bottomSection}>
        <div style={styles.bottomContentWrapper}>
          {bottomContent}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    backgroundColor: '#f8f9fa',
    position: 'relative',
  },
  topSection: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    borderBottom: '1px solid #eee',
  },
  topContentWrapper: {
    width: '100%',
    height: '100%',
  },
  handle: {
    height: `${HANDLE_HEIGHT}px`,
    width: '100%',
    backgroundColor: '#f1f3f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    touchAction: 'none',
    zIndex: 10,
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  handleBar: {
    width: '40px',
    height: '4px',
    backgroundColor: '#adb5bd',
    borderRadius: '2px',
  },
  bottomSection: {
    flex: 1,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  bottomContentWrapper: {
    height: '100%',
    width: '100%',
  },
};
